import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ReportService } from 'src/app/core/helpers/report.service';
import { Chart } from 'chart.js';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss']
})
export class ReportComponent implements OnInit {

  // ── Auth ───────────────────────────────────────────────────────────────────
  isAuthenticated = false;
  passwordInput = '';
  authError = '';
  reportPassword = '';

  // ── Filters ────────────────────────────────────────────────────────────────
  startDate: Date;
  endDate: Date;
  trendMode: 'daily' | 'weekly' = 'daily';
  loading = false;
  loadError = '';

  // ── Data ───────────────────────────────────────────────────────────────────
  registrationsData: any = null;
  gamesData: any = null;
  optinsData: any = null;
  optinRateData: any = null;
  rewardsData: any = null;

  // ── Chart canvas refs ──────────────────────────────────────────────────────
  @ViewChild('registrationsCanvas', { static: false }) registrationsCanvas: ElementRef;
  @ViewChild('gamesCanvas', { static: false }) gamesCanvas: ElementRef;
  @ViewChild('optinsCanvas', { static: false }) optinsCanvas: ElementRef;
  @ViewChild('rewardsCanvas', { static: false }) rewardsCanvas: ElementRef;
  @ViewChild('productCanvas', { static: false }) productCanvas: ElementRef;

  private charts: { [key: string]: Chart } = {};

  constructor(private readonly reportService: ReportService) {}

  ngOnInit() {
    const today = new Date();
    this.endDate = new Date(today);
    this.startDate = new Date(today.getFullYear(), today.getMonth(), 1); // First of month
  }

  // ── Password gate ──────────────────────────────────────────────────────────

  authenticate() {
    if (!this.passwordInput || !this.passwordInput.trim()) {
      this.authError = 'Please enter the report password.';
      return;
    }
    this.authError = '';
    this.reportPassword = this.passwordInput.trim();

    // Verify password by making a lightweight API call
    const start = this.formatDate(this.startDate);
    const end = this.formatDate(this.endDate);
    this.reportService.getOptinRate(start, end, this.reportPassword)
      .then(() => {
        this.isAuthenticated = true;
        this.loadAll();
      })
      .catch((err) => {
        if (err && err.status === 401) {
          this.authError = 'Incorrect password. Please try again.';
        } else {
          this.authError = 'Could not connect to the server. Please try again.';
        }
        this.reportPassword = '';
      });
  }

  // ── Load all reports ───────────────────────────────────────────────────────

  loadAll() {
    const start = this.formatDate(this.startDate);
    const end = this.formatDate(this.endDate);
    this.loading = true;
    this.loadError = '';

    Promise.all([
      this.reportService.getRegistrations(start, end, this.reportPassword),
      this.reportService.getGames(start, end, this.reportPassword),
      this.reportService.getMarketingOptins(start, end, this.reportPassword),
      this.reportService.getOptinRate(start, end, this.reportPassword),
      this.reportService.getRewards(start, end, this.reportPassword)
    ])
      .then(([reg, games, optins, optinRate, rewards]) => {
        this.registrationsData = reg;
        this.gamesData = games;
        this.optinsData = optins;
        this.optinRateData = optinRate;
        this.rewardsData = rewards;
        this.loading = false;
        setTimeout(() => this.renderCharts(), 0);
      })
      .catch(() => {
        this.loading = false;
        this.loadError = 'Failed to load report data. Please try again.';
      });
  }

  setTrendMode(mode: 'daily' | 'weekly') {
    this.trendMode = mode;
    this.renderCharts();
  }

  // ── Exports ────────────────────────────────────────────────────────────────

  exportOptins() {
    const start = this.formatDate(this.startDate);
    const end = this.formatDate(this.endDate);
    this.reportService.exportMarketingOptins(start, end, this.reportPassword)
      .then((blob: Blob) => {
        const url = globalThis.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `marketing-optins-${start}-${end}.csv`;
        a.click();
        globalThis.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Export failed. Please try again.'));
  }

  // ── Chart rendering ────────────────────────────────────────────────────────

  private renderCharts() {
    if (this.registrationsData) { this.renderBar('registrationsCanvas', 'Registrations', this.getTrend(this.registrationsData)); }
    if (this.gamesData)         { this.renderBar('gamesCanvas', 'Games Played', this.getTrend(this.gamesData)); }
    if (this.optinsData)        { this.renderLine('optinsCanvas', 'Marketing Opt-ins', this.getTrend(this.optinsData)); }
    if (this.rewardsData)       { this.renderBar('rewardsCanvas', 'Rewards Won', this.getTrend(this.rewardsData)); }
    if (this.rewardsData && this.rewardsData.ByProduct && this.rewardsData.ByProduct.length) {
      this.renderDoughnut('productCanvas');
    }
  }

  private getTrend(data: any): { labels: string[]; counts: number[] } {
    const trend = this.trendMode === 'daily' ? data.DailyTrend : data.WeeklyTrend;
    if (!trend || !trend.length) { return { labels: [], counts: [] }; }
    if (this.trendMode === 'daily') {
      return {
        labels: trend.map((t: any) => t.Date),
        counts: trend.map((t: any) => t.Count)
      };
    } else {
      return {
        labels: trend.map((t: any) => `W${t.Week}/${t.Year}`),
        counts: trend.map((t: any) => t.Count)
      };
    }
  }

  private readonly chartFontSize = 14;

  private axisTickOptions() {
    return { fontSize: this.chartFontSize, beginAtZero: true, precision: 0 };
  }

  private renderBar(refKey: string, label: string, trend: { labels: string[]; counts: number[] }) {
    const canvas = this[refKey] as ElementRef;
    if (!canvas) { return; }
    if (this.charts[refKey]) { this.charts[refKey].destroy(); }
    this.charts[refKey] = new Chart(canvas.nativeElement, {
      type: 'bar',
      data: {
        labels: trend.labels,
        datasets: [{
          label,
          data: trend.counts,
          backgroundColor: 'rgba(54, 162, 235, 0.7)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { labels: { fontSize: this.chartFontSize } },
        scales: {
          xAxes: [{ ticks: { fontSize: this.chartFontSize, maxRotation: 45, autoSkip: true, autoSkipPadding: 12 } }],
          yAxes: [{ ticks: this.axisTickOptions() }]
        }
      }
    });
  }

  private renderLine(refKey: string, label: string, trend: { labels: string[]; counts: number[] }) {
    const canvas = this[refKey] as ElementRef;
    if (!canvas) { return; }
    if (this.charts[refKey]) { this.charts[refKey].destroy(); }
    this.charts[refKey] = new Chart(canvas.nativeElement, {
      type: 'line',
      data: {
        labels: trend.labels,
        datasets: [{
          label,
          data: trend.counts,
          fill: true,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          pointBackgroundColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { labels: { fontSize: this.chartFontSize } },
        scales: {
          xAxes: [{ ticks: { fontSize: this.chartFontSize, maxRotation: 45, autoSkip: true, autoSkipPadding: 12 } }],
          yAxes: [{ ticks: this.axisTickOptions() }]
        }
      }
    });
  }

  private renderDoughnut(refKey: string) {
    const canvas = this[refKey] as ElementRef;
    if (!canvas) { return; }
    if (this.charts[refKey]) { this.charts[refKey].destroy(); }
    const byProduct: any[] = this.rewardsData.ByProduct;
    const palette = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#C9CBCF', '#E7E9ED', '#71B37C', '#F7464A'
    ];
    this.charts[refKey] = new Chart(canvas.nativeElement, {
      type: 'doughnut',
      data: {
        labels: byProduct.map(p => p.Product),
        datasets: [{
          data: byProduct.map(p => p.Count),
          backgroundColor: byProduct.map((_, i) => palette[i % palette.length])
        }]
      },
      options: {
        responsive: true,
        legend: { position: 'right', labels: { fontSize: this.chartFontSize } }
      }
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private formatDate(d: Date): string {
    if (!d) { return ''; }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  get optinRateFormatted(): string {
    if (!this.optinRateData) { return '-'; }
    return `${this.optinRateData.OptinRatePercentage}%`;
  }

  get optinRateDeg(): number {
    if (!this.optinRateData) { return 0; }
    return Math.min(360, (this.optinRateData.OptinRatePercentage / 100) * 360);
  }
}
